import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEye, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';

import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { RoleService } from '../../services/role.service';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../shared/constants/pagination.constants';

@Component({
    selector: 'app-sales-installation-partners',
    imports: [CommonModule, FormsModule, FontAwesomeModule],
    templateUrl: './sales-installation-partners.component.html',
    styleUrls: ['../../lead-management/lead-management.shared.css']
})
export class SalesInstallationPartnersComponent implements OnInit {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  partners: any[] = [];
  loading = false;
  isSuperUser = false;
  showDeleteModal = false;
  deleting = false;
  selectedPartner: any = null;
  currentPage = 1;
  pageSize = 10;
  totalRecords = 0;
  nextPage: string | null = null;
  previousPage: string | null = null;
  filters: any = {
    search: '',
    status: '',
    business_type: '',
    city: '',
    state: '',
  };

  statuses = [
    ['LEAD', 'Lead'],
    ['DEMO_SCHEDULED', 'Demo Scheduled'],
    ['DEMO_COMPLETED', 'Demo Completed'],
    ['INTERESTED', 'Interested'],
    ['COMMERCIAL_DISCUSSION', 'Commercial Discussion'],
    ['AGREEMENT_PENDING', 'Agreement Pending'],
    ['ONBOARDED', 'Onboarded'],
    ['ACTIVE', 'Active'],
    ['INACTIVE', 'Inactive'],
    ['REJECTED', 'Rejected'],
  ];

  businessTypes = [
    ['CCTV_VENDOR', 'CCTV Vendor'],
    ['SECURITY_INTEGRATOR', 'Security Integrator'],
    ['IT_VENDOR', 'IT Vendor'],
    ['HARDWARE_INSTALLER', 'Hardware Installer'],
    ['SYSTEM_INTEGRATOR', 'System Integrator'],
    ['OTHER', 'Other'],
  ];

  constructor(
    private apiService: ApiService,
    private router: Router,
    private authService: AuthService,
    private roleService: RoleService,
    private toastr: ToastrService,
    library: FaIconLibrary,
  ) {
    library.addIcons(faEye, faPen, faTrash);
  }

  ngOnInit(): void {
    const groups = this.authService.getCurrentUserInfo()?.role;
    this.isSuperUser = this.roleService.isSuperUser(groups);
    this.loadPartners();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
  }

  loadPartners(): void {
    this.loading = true;
    this.apiService.listSalesInstallationPartners(this.currentPage, this.pageSize, this.filters).subscribe({
      next: (response: any) => {
        this.partners = Array.isArray(response)
          ? response
          : response?.results || response?.data || [];
        this.totalRecords = Array.isArray(response) ? this.partners.length : response?.count || 0;
        this.nextPage = Array.isArray(response) ? null : response?.next || null;
        this.previousPage = Array.isArray(response) ? null : response?.previous || null;
        this.loading = false;
      },
      error: () => {
        this.partners = [];
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadPartners();
  }

  resetFilters(): void {
    this.filters = { search: '', status: '', business_type: '', city: '', state: '' };
    this.applyFilters();
  }

  openCreate(): void {
    this.router.navigate(['admin/sales-installation-partners/new']);
  }

  openView(partner: any): void {
    this.router.navigate(['admin/sales-installation-partners', partner.id]);
  }

  openEdit(partner: any): void {
    this.router.navigate(['admin/sales-installation-partners', partner.id, 'edit']);
  }

  openDelete(partner: any): void {
    if (!this.isSuperUser) {
      return;
    }
    this.selectedPartner = partner;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    if (this.deleting) {
      return;
    }
    this.showDeleteModal = false;
    this.selectedPartner = null;
  }

  confirmDelete(): void {
    if (!this.selectedPartner?.id || this.deleting) {
      return;
    }
    this.deleting = true;
    this.apiService.deleteSalesInstallationPartner(this.selectedPartner.id).subscribe({
      next: () => {
        this.deleting = false;
        this.showDeleteModal = false;
        this.selectedPartner = null;
        this.toastr.success('Sales & Installation Partner deleted successfully.', 'Success');
        if (this.partners.length === 1 && this.currentPage > 1) {
          this.currentPage -= 1;
        }
        this.loadPartners();
      },
      error: (err) => {
        this.deleting = false;
        const message = err?.error?.detail || 'Unable to delete Sales & Installation Partner.';
        this.toastr.error(message, 'Error');
      }
    });
  }

  previous(): void {
    if (!this.previousPage) {
      return;
    }
    this.currentPage -= 1;
    this.loadPartners();
  }

  next(): void {
    if (!this.nextPage) {
      return;
    }
    this.currentPage += 1;
    this.loadPartners();
  }

  selectPageSize(): void {
    this.currentPage = 1;
    this.loadPartners();
  }
}
